import { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { AiSuggestionsRegenerationTriggerValue } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	useChatStoreActions,
	useConversationById,
} from '@atlassian/conversation-assistant/controllers/chat-store';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import type { ThinkingStatus } from '../../pm-plugins/thinking-plugin';
import {
	fireAgentDeactivatedErrorAnalytics,
	fireAgentExternalConfigReferenceErrorAnalytics,
	fireConversationSetupErrorAnalytics,
	fireStreamErrorAnalytics,
} from '../utils/analytics';
import { getConfiguredContentReviewerAgentIds } from '../utils/content-reviewer-agent-config';
import { mapStreamErrorToTemplate } from '../utils/mapThinkingError';
import { toReviewingAgent } from '../utils/reviewingAgent';
import {
	type ContentReviewerAgent,
	useContentReviewerAgentResolver,
} from './useContentReviewerAgentResolver';

/**
 * Builds the ADF prompt that triggers the add-suggestions-to-content skill. The text that
 * precedes the inline skill mention is passed in (already-localised) so the
 * prompt the AI service receives matches the user's locale.
 */
export const buildSuggestEditsPrompt = (precedingText: string): DocNode => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [
				{ type: 'text', text: precedingText },
				{
					type: 'inlineExtension',
					attrs: {
						extensionKey: 'skill:skillTag',
						extensionType: 'com.atlassian.rovo.skill',
						parameters: { slug: 'add-suggestions-to-content' },
					},
				},
			],
		},
	],
});

/**
 * Builds the prompt used once the Content Reviewer agent handles the review. The agent
 * has the add-suggestions-to-content behaviour built in, so the prompt is plain text with
 * no inline skill mention. The text is passed in already-localised.
 */
export const buildAgentReviewPrompt = (text: string): DocNode => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [{ type: 'text', text }],
		},
	],
});

type UseSkillConversationParams = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	isRovoMounted: boolean;
	thinkingStatus: ThinkingStatus | undefined;
};

/**
 * Owns the conversation lifecycle for the add-suggestions-to-content skill triggered by the
 * Thinking surface:
 *  - When `thinkingStatus` flips to `'thinking'`, resolve the Content Reviewer
 *    agent, create an optimistic conversation and dispatch the review prompt at
 *    that agent. When the agent can't be resolved, complete the session without
 *    sending.
 *  - Watch the conversation status and transition the editor state:
 *      idle  → complete (or → stop, if Rovo Chat is open)
 *      error → stop
 *  - When `thinkingStatus` returns to `'idle'`, abort any in-flight request
 *    and clear the active conversation id.
 *  - While Rovo is mounted and we're still thinking, surface the conversation
 *    in the Rovo chat panel via `setSelectedConversation`.
 */
export function useSkill({ api, thinkingStatus, isRovoMounted }: UseSkillConversationParams): void {
	const { formatMessage } = useIntl();
	const {
		createConversationOptimistically,
		sendMessage,
		abortRequests,
		setConversationData,
		setSelectedConversation,
	} = useChatStoreActions();

	// Resolves (and provisions) the agent the prompt is sent at. Stable, so it can't re-trigger
	// the send effect mid-session.
	const { resolveAgent } = useContentReviewerAgentResolver({ api });

	// The legacy route owns its optimistic id. Content Reviewer starts from AI Mate's
	// supported empty slot and records the canonical id minted by the first-message flow.
	const conversationIdRef = useRef<string | null>(null);
	if (
		!fg('content_reviewer_agent') &&
		thinkingStatus === 'thinking' &&
		!conversationIdRef.current
	) {
		conversationIdRef.current = crypto.randomUUID();
	}
	const legacyConversationId = conversationIdRef.current;
	const contentReviewerConversationIdRef = useRef<string | null>(null);
	const [contentReviewerConversationId, setContentReviewerConversationId] = useState<string | null>(
		null,
	);
	const parallelConversationIdsRef = useRef<string[]>([]);
	const parallelSessionRef = useRef<object | null>(null);
	const sessionRef = useRef<object | null>(null);
	const completedSessionRef = useRef<object | null>(null);
	if (thinkingStatus === 'thinking' && !sessionRef.current) {
		sessionRef.current = {};
	}
	const session = thinkingStatus === 'thinking' ? sessionRef.current : null;

	const activeConversation = useConversationById({
		conversationId: fg('content_reviewer_agent')
			? (contentReviewerConversationId ?? '')
			: (legacyConversationId ?? ''),
	});

	// The stream callbacks are created once at send time but fire later, after Rovo
	// may have mounted/unmounted. Read the latest value through a ref so completion
	// branches on the current mount state (matching the old status-driven effect).
	const isRovoMountedRef = useRef(isRovoMounted);
	isRovoMountedRef.current = isRovoMounted;

	/**
	 * Backend assigned assistant_message_id to be used as
	 * singleInstrumentationId for Unified Instrumentation Events.
	 *
	 * Stub for now until we have unified analytics
	 */
	const [_, setAssistantMessageId] = useState<string | undefined>(undefined);

	// Kick off the add-suggestions-to-content skill once per thinking session.
	useEffect(() => {
		if (!session) {
			return;
		}

		const promptPrefix = formatMessage(aiSuggestionsMessages.suggestEditsPromptPrefix);
		const agentPrompt = formatMessage(aiSuggestionsMessages.contentReviewAgentPrompt);
		let didFireStreamErrorAnalytics = false;
		let cancelled = false;

		/**
		 * Provisioning and resolving the agent are slow, so the session can end while
		 * `run` is still between awaits. Everything that dispatches after an await —
		 * sending the prompt, driving the thinking state — has to check this first.
		 * The ref no longer holding this session's id means the abort effect cleared
		 * it (user stopped) or a later render minted a new one; the flag covers this
		 * effect being torn down, which the ref alone doesn't catch.
		 */
		const isCancelled = (): boolean => cancelled || sessionRef.current !== session;
		const runParallelConfiguredAgents = async (agentIds: string[]) => {
			parallelSessionRef.current = session;
			const resolved = await Promise.all(
				agentIds.map(async (agentId) => {
					try {
						return await resolveAgent({ agentId });
					} catch {
						return undefined;
					}
				}),
			);
			if (isCancelled()) {
				return;
			}
			const agents = resolved.filter((agent): agent is ContentReviewerAgent => agent !== undefined);
			if (!agents.length) {
				fireAgentExternalConfigReferenceErrorAnalytics(api?.analytics?.actions);
				api?.aiSuggestions?.actions.setThinkingState('error');
				return;
			}

			// AI Mate mints a fresh local ID for each first-message send from its empty slot.
			contentReviewerConversationIdRef.current = EMPTY_CONVERSATION_ID;
			setContentReviewerConversationId(EMPTY_CONVERSATION_ID);
			const reviewingAgent = toReviewingAgent(agents[0]);
			if (reviewingAgent) {
				api?.aiSuggestions?.actions.setReviewingAgent(reviewingAgent);
			}

			const settled = new Set<number>();
			let hasSucceeded = false;
			let lastError: unknown;
			const settle = async (index: number, succeeded: boolean, error?: unknown) => {
				if (isCancelled() || settled.has(index)) {
					return;
				}
				settled.add(index);
				hasSucceeded ||= succeeded;
				if (!succeeded) {
					lastError = error;
				}
				if (settled.size !== agents.length) {
					return;
				}
				if (hasSucceeded) {
					completedSessionRef.current = session;
				}
				if (!hasSucceeded) {
					api?.aiSuggestions?.actions.setThinkingState('error', {
						template: mapStreamErrorToTemplate(lastError),
					});
				} else if (isRovoMountedRef.current) {
					api?.aiSuggestions?.actions.setThinkingState('idle');
				} else {
					const refreshed = await api?.aiSuggestions?.actions
						.refreshSuggestions({
							regenerationTrigger: AiSuggestionsRegenerationTriggerValue.REVIEW_COMPLETED,
						})
						.catch(() => false);
					if (!isCancelled()) {
						api?.aiSuggestions?.actions.setThinkingState(refreshed ? 'complete' : 'error');
					}
				}
			};

			// Start every send before awaiting any stream, so the agents run in parallel.
			await Promise.all(
				agents.map(async (agent, index) => {
					try {
						await sendMessage({
							prompt: buildAgentReviewPrompt(agentPrompt),
							conversationId: EMPTY_CONVERSATION_ID,
							agent,
							storeUserMessages: true,
							fastModeEnabled: true,
							addCurrentPageAsContextUserPreference: true,
							onTempMessageIdReplacedOverride: (_, newId) => {
								if (index === 0) {
									setAssistantMessageId(newId);
								}
							},
							onNewConversationIdAvailable: (canonicalId: string) => {
								if (isCancelled()) {
									return;
								}
								parallelConversationIdsRef.current[index] = canonicalId;
								if (index === 0) {
									contentReviewerConversationIdRef.current = canonicalId;
									setContentReviewerConversationId(canonicalId);
								}
							},
							promptStreamHook: {
								onFinalResponse: () => settle(index, true),
								onError: (error) => {
									if (!isCancelled()) {
										didFireStreamErrorAnalytics = true;
										fireStreamErrorAnalytics(api?.analytics?.actions, error);
									}
									return settle(index, false, error);
								},
							},
						});
						// A send that ends without a final response cannot leave the toolbar spinning.
						await settle(index, false);
					} catch (error) {
						if (!isCancelled() && !settled.has(index)) {
							fireConversationSetupErrorAnalytics(api?.analytics?.actions, error);
						}
						await settle(index, false, error);
					}
				}),
			);
		};

		const run = async () => {
			const configuredAgentIds = fg('content_reviewer_agent')
				? getConfiguredContentReviewerAgentIds()
				: undefined;
			if (configuredAgentIds && configuredAgentIds.length > 1) {
				await runParallelConfiguredAgents(configuredAgentIds);
				return;
			}
			let contentReviewerAgent: ContentReviewerAgent | undefined;
			if (fg('content_reviewer_agent')) {
				contentReviewerConversationIdRef.current = EMPTY_CONVERSATION_ID;
				setContentReviewerConversationId(EMPTY_CONVERSATION_ID);
				await createConversationOptimistically({
					id: EMPTY_CONVERSATION_ID,
					status: CHAT_STORE_STATUS.loadingChat,
				});

				if (isCancelled()) {
					return;
				}

				contentReviewerAgent = await resolveAgent();

				// The empty-agent branch below dispatches and then returns, so it never
				// reaches the check before the send — it needs its own.
				if (isCancelled()) {
					return;
				}

				if (!contentReviewerAgent) {
					// No agent to run the skill: clean up the store and show the error screen.
					await setConversationData({
						conversationId: EMPTY_CONVERSATION_ID,
						data: { status: CHAT_STORE_STATUS.idle },
					});
					fireAgentExternalConfigReferenceErrorAnalytics(api?.analytics?.actions);
					api?.aiSuggestions?.actions.setThinkingState('error');
					return;
				}

				// Publish the agent actually handling the review, so every loading surface can
				// name it. This is the authoritative resolution — unlike the pre-resolve in the
				// thinking bar it provisions, so it's also what lands on a site's first review.
				const reviewingAgent = toReviewingAgent(contentReviewerAgent);
				if (reviewingAgent) {
					api?.aiSuggestions?.actions.setReviewingAgent(reviewingAgent);
				}

				await setConversationData({
					conversationId: EMPTY_CONVERSATION_ID,
					data: {
						sourceAgentId: contentReviewerAgent.id,
						agent: {
							agent_id: contentReviewerAgent.id,
							agent_named_id:
								contentReviewerAgent.external_config_reference ?? contentReviewerAgent.named_id,
							agent_name: contentReviewerAgent.name,
							creator_type: contentReviewerAgent.creator_type,
							is_default: contentReviewerAgent.is_default,
							identity_account_id: contentReviewerAgent.identity_account_id ?? undefined,
						},
					},
				});
			} else if (legacyConversationId) {
				await createConversationOptimistically({ id: legacyConversationId });
			}

			if (isCancelled()) {
				return;
			}

			await sendMessage({
				// The agent has the add-suggestions-to-content skill built in, so it only needs a
				// plain review prompt. Without the agent the skill has to be invoked explicitly.
				prompt: fg('content_reviewer_agent')
					? buildAgentReviewPrompt(agentPrompt)
					: buildSuggestEditsPrompt(promptPrefix),
				conversationId: fg('content_reviewer_agent')
					? EMPTY_CONVERSATION_ID
					: (legacyConversationId ?? undefined),
				// If contentReviewerAgent is undefined it won't go to agent
				agent: contentReviewerAgent,
				storeUserMessages: true,
				fastModeEnabled: true,
				addCurrentPageAsContextUserPreference: true,
				/**
				 * This fires when the temporary message id is replaced with the
				 * backend-assigned assistant_message_id.
				 */
				onTempMessageIdReplacedOverride: (_, newId) => {
					setAssistantMessageId(newId);
				},
				...(fg('content_reviewer_agent')
					? {
							onNewConversationIdAvailable: (conversationId: string) => {
								if (isCancelled()) {
									return;
								}
								contentReviewerConversationIdRef.current = conversationId;
								setContentReviewerConversationId(conversationId);
							},
						}
					: {}),
				promptStreamHook: {
					/**
					 * Fires when the FINAL_RESPONSE stream chunk is processed — the
					 * add-suggestions-to-content skill has produced its answer.
					 *   - Rovo mounted   → setThinkingState('idle') (Rovo chat takes over, no bar)
					 *   - Rovo unmounted → refreshSuggestions then setThinkingState('complete')
					 *
					 * Ignored once cancelled, so a late response can't reopen the bar.
					 */
					onFinalResponse: async () => {
						if (isCancelled()) {
							return;
						}
						completedSessionRef.current = session;
						if (isRovoMountedRef.current) {
							api?.aiSuggestions?.actions.setThinkingState('idle');
						} else {
							const refreshed = await api?.aiSuggestions?.actions
								.refreshSuggestions({
									regenerationTrigger: AiSuggestionsRegenerationTriggerValue.REVIEW_COMPLETED,
								})
								.catch(() => false);
							if (isCancelled()) {
								return;
							}
							api?.aiSuggestions?.actions.setThinkingState(refreshed ? 'complete' : 'error');
						}
					},
					/**
					 * Fires on an in-stream ERROR / ACTION_ERROR chunk, and on send rejection —
					 * which includes the `AbortError` from stopping, hence the cancel check.
					 */
					onError: (error) => {
						if (isCancelled()) {
							return Promise.resolve();
						}
						didFireStreamErrorAnalytics = true;
						fireStreamErrorAnalytics(api?.analytics?.actions, error);
						// Classify the backend error so the bar shows the matching copy/links.
						api?.aiSuggestions?.actions.setThinkingState('error', {
							template: mapStreamErrorToTemplate(error),
						});
						return Promise.resolve();
					},
				},
			});
		};

		run().catch((error) => {
			// Stopping the session aborts the in-flight request, so the rejection that
			// follows is the expected outcome of a user cancellation rather than a
			// failure worth reporting — and stopping again would end whichever session
			// is current by now.
			if (isCancelled()) {
				return;
			}
			if (!didFireStreamErrorAnalytics) {
				fireConversationSetupErrorAnalytics(api?.analytics?.actions, error);
			}
			if (parallelSessionRef.current === session) {
				parallelConversationIdsRef.current.forEach((conversationId) => {
					void setConversationData({
						conversationId,
						data: { status: CHAT_STORE_STATUS.idle },
					});
				});
			} else if (fg('content_reviewer_agent')) {
				setConversationData({
					conversationId: contentReviewerConversationIdRef.current ?? EMPTY_CONVERSATION_ID,
					data: { status: CHAT_STORE_STATUS.idle },
				});
			}
			api?.aiSuggestions?.actions.setThinkingState('error');
		});

		return () => {
			cancelled = true;
		};
	}, [
		session,
		api,
		createConversationOptimistically,
		legacyConversationId,
		sendMessage,
		setConversationData,
		formatMessage,
		resolveAgent,
	]);

	// Clear the conversation id whenever a thinking session ends (both 'idle' and
	// 'complete') so the next "Review" mints a fresh id and re-triggers the skill.
	// Abort sessions that end before a final response. FINAL_RESPONSE can update
	// the UI before the send action clears its abort controller, so controller
	// presence alone does not mean the user cancelled the review.
	useEffect(() => {
		if (thinkingStatus === 'thinking') {
			return;
		}
		if (completedSessionRef.current !== sessionRef.current) {
			if (parallelSessionRef.current) {
				parallelConversationIdsRef.current.forEach((conversationId) => {
					abortRequests?.({ conversationId });
				});
			} else {
				const conversationId = fg('content_reviewer_agent')
					? contentReviewerConversationIdRef.current
					: conversationIdRef.current;
				if (conversationId) {
					abortRequests?.({ conversationId });
				}
			}
		}
		parallelConversationIdsRef.current = [];
		parallelSessionRef.current = null;
		conversationIdRef.current = null;
		contentReviewerConversationIdRef.current = null;
		completedSessionRef.current = null;
		setContentReviewerConversationId(null);
		sessionRef.current = null;
		setAssistantMessageId(undefined);
	}, [thinkingStatus, abortRequests]);

	// React to conversation status changes reported by the store and drive the
	// editor thinking state accordingly:
	//   - store idle + Rovo mounted   → setThinkingState('idle') (Rovo chat takes over)
	//   - store idle + Rovo unmounted → setThinkingState('complete')
	//   - deactivatedAgentError       → setThinkingState('error')
	useEffect(() => {
		/**
		 * Note - because promptStreamHook already handles onFinalResponse,
		 * this hook shouldn't need to do anything unless the conversation
		 * somehow terminates without a final response (e.g. deactivatedAgentError)
		 * or has an error that isn't caught by onError.
		 */
		// A multi-agent review settles only when all streams have finished.
		if (parallelSessionRef.current) {
			return;
		}
		if (!activeConversation) {
			return;
		}
		if (
			fg('content_reviewer_agent') &&
			activeConversation.id !== contentReviewerConversationIdRef.current
		) {
			return;
		}

		let cancelled = false;

		switch (activeConversation.status) {
			case CHAT_STORE_STATUS.idle: {
				// Returning to idle mid-thinking means the session finished. The first
				// idle has lastAgentMessageStatus undefined; once set, it's done.
				if (
					thinkingStatus === 'thinking' &&
					activeConversation.lastAgentMessageStatus !== undefined
				) {
					// FAILED/CANCELLED means no suggestions — show the error screen
					// instead of completing (which would overwrite the error state).
					const lastAgentMessageFailed =
						activeConversation.lastAgentMessageStatus === 'FAILED' ||
						activeConversation.lastAgentMessageStatus === 'CANCELLED';
					if (activeConversation.lastAgentMessageStatus === 'SUCCESSFUL') {
						completedSessionRef.current = sessionRef.current;
					}

					if (lastAgentMessageFailed) {
						api?.aiSuggestions?.actions.setThinkingState('error');
					} else if (isRovoMountedRef.current) {
						// Rovo chat is open, so don't show the thinking bar on completion.
						api?.aiSuggestions?.actions.setThinkingState('idle');
					} else {
						void api?.aiSuggestions?.actions
							.refreshSuggestions({
								regenerationTrigger: AiSuggestionsRegenerationTriggerValue.REVIEW_COMPLETED,
							})
							.catch(() => false)
							.then((refreshed) => {
								if (cancelled) {
									return;
								}
								api?.aiSuggestions?.actions.setThinkingState(refreshed ? 'complete' : 'error');
							});
					}
				} else if (
					thinkingStatus === 'thinking' &&
					!fg('content_reviewer_agent') &&
					isRovoMountedRef.current
				) {
					api?.aiSuggestions?.actions.setThinkingState('idle');
				}
				break;
			}
			case CHAT_STORE_STATUS.deactivatedAgentError: {
				fireAgentDeactivatedErrorAnalytics(api?.analytics?.actions);
				api?.aiSuggestions?.actions.setThinkingState('error');
				break;
			}
		}
		return () => {
			cancelled = true;
		};
	}, [activeConversation, api, thinkingStatus]);

	// When Rovo chat is open and a conversation is active, surface it in the
	// Rovo panel so the user can follow along in real time.
	useEffect(() => {
		const conversationId = fg('content_reviewer_agent')
			? contentReviewerConversationId
			: legacyConversationId;
		if (isRovoMounted && thinkingStatus !== 'idle' && conversationId) {
			setSelectedConversation({ conversationId });
		}
	}, [
		contentReviewerConversationId,
		isRovoMounted,
		legacyConversationId,
		setSelectedConversation,
		thinkingStatus,
	]);
}
